import React, { useState } from "react";
import { CreditCard, AlertCircle, Smartphone, DollarSign, QrCode } from "lucide-react";
import usePaymentMethods from "../hooks/usePaymentMethods";
import { PaymentService, PaymentMethod } from "../services/paymentService";
import useToastStore from "../store/useToastStore";

interface ModalProps {
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
}

// Components
const Modal: React.FC<ModalProps> = ({ isOpen, title, message, onConfirm, onCancel }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
                <h2 className="text-lg font-semibold mb-4">{title}</h2>
                <p className="mb-6 text-gray-600">{message}</p>
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 w-full sm:w-auto"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 w-full sm:w-auto"
                    >
                        Confirm
                    </button>
                </div>
            </div>
        </div>
    );
};

// Payment method configuration interface
interface PaymentMethodConfig {
    type: string;
    name: string;
    description: string;
    icon: React.ReactNode;
    iconBgColor: string;
    iconColor: string;
    requiresConfig: boolean;
    configFields?: {
        key: string;
        label: string;
        type: string;
        placeholder: string;
        example?: string;
    }[];
    infoMessage?: {
        title: string;
        message: string;
        bgColor: string;
        borderColor: string;
        textColor: string;
        iconColor: string;
    };
}

// Payment method configurations
const PAYMENT_METHOD_CONFIGS: Record<string, PaymentMethodConfig> = {
    RAZORPAY: {
        type: "RAZORPAY",
        name: "Razorpay",
        description: "Accept credit/debit cards and other digital payment methods",
        icon: <CreditCard className="h-5 w-5" />,
        iconBgColor: "bg-green-100",
        iconColor: "text-green-600",
        requiresConfig: true,
        configFields: [
            {
                key: "keyId",
                label: "Key ID",
                type: "text",
                placeholder: "Enter Razorpay Key ID"
            },
            {
                key: "secretKey",
                label: "Secret Key",
                type: "password",
                placeholder: "Enter Razorpay Secret Key"
            }
        ]
    },
    PHONEPE: {
        type: "PHONEPE",
        name: "PhonePe",
        description: "Accept payments through PhonePe UPI, cards, and other digital payment methods",
        icon: <Smartphone className="h-5 w-5" />,
        iconBgColor: "bg-purple-100",
        iconColor: "text-purple-600",
        requiresConfig: true,
        configFields: [
            {
                key: "clientId",
                label: "Client ID",
                type: "text",
                placeholder: "Enter PhonePe Client ID"
            },
            {
                key: "clientSecret",
                label: "Client Secret",
                type: "password",
                placeholder: "Enter PhonePe Client Secret"
            }
        ]
    },
    COD: {
        type: "COD",
        name: "COD (Cash on Delivery)",
        description: "Allow customers to pay with cash when their order is delivered",
        icon: <DollarSign className="h-5 w-5" />,
        iconBgColor: "bg-orange-100",
        iconColor: "text-orange-600",
        requiresConfig: false,
        infoMessage: {
            title: "Important Note",
            message: "COD orders require manual verification and cash collection upon delivery. Make sure your delivery team is prepared to handle cash transactions.",
            bgColor: "bg-orange-50",
            borderColor: "border-orange-200",
            textColor: "text-orange-700",
            iconColor: "text-orange-600"
        }
    },
    MANUAL_UPI: {
        type: "MANUAL_UPI",
        name: "Manual UPI Payment",
        description: "Display your UPI ID for customers to make manual payments",
        icon: <QrCode className="h-5 w-5" />,
        iconBgColor: "bg-blue-100",
        iconColor: "text-blue-600",
        requiresConfig: true,
        configFields: [
            {
                key: "upiId",
                label: "UPI ID",
                type: "text",
                placeholder: "Enter your UPI ID (e.g., yourname@upi)",
                example: "Example: 7518388162@ybl, yourname@okicici, etc."
            }
        ],
        infoMessage: {
            title: "How it works",
            message: "Customers will see your UPI ID during checkout and can make payments directly to your UPI ID. You'll need to manually verify payments and update order status.",
            bgColor: "bg-blue-50",
            borderColor: "border-blue-200",
            textColor: "text-blue-700",
            iconColor: "text-blue-600"
        }
    }
};

// Unified Payment Method Card Component
const PaymentMethodCard: React.FC<{
    method: PaymentMethod;
    onToggle: () => void;
}> = ({ method, onToggle }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [configValues, setConfigValues] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);
    const { refetch } = usePaymentMethods();
    const toast = useToastStore();

    const config = PAYMENT_METHOD_CONFIGS[method.type];

    // Initialize config values from method config
    React.useEffect(() => {
        if (method.config && config?.configFields) {
            const values: Record<string, string> = {};
            config.configFields.forEach(field => {
                const value = method.config?.[field.key as keyof typeof method.config];
                values[field.key] = typeof value === 'string' ? value : "";
            });
            setConfigValues(values);
        }
    }, [method.config, config?.configFields]);

    if (!config) return null;

    const handleSave = async () => {
        // Validate required fields
        if (config.requiresConfig && config.configFields) {
            const missingFields = config.configFields.filter(field => !configValues[field.key]?.trim());
            if (missingFields.length > 0) {
                toast.showToast(`Please fill in all required fields: ${missingFields.map(f => f.label).join(', ')}`, "error");
                return;
            }
        }

        setIsSaving(true);
        try {
            switch (method.type) {
                case "RAZORPAY":
                    await PaymentService.updateRazorpayConfig(configValues.keyId || "", configValues.secretKey || "");
                    break;
                case "PHONEPE":
                    await PaymentService.updatePhonePeConfig(configValues.clientId || "", configValues.clientSecret || "");
                    break;
                case "COD":
                    await PaymentService.updateCodConfig(!method.enabled);
                    break;
                case "MANUAL_UPI":
                    await PaymentService.updateManualUpiConfig(configValues.upiId || "");
                    break;
                default:
                    throw new Error(`Unsupported payment method type: ${method.type}`);
            }

            await refetch();
            setIsEditing(false);
            toast.showToast(`${config.name} configuration updated successfully`, "success");
        } catch (error) {
            console.error(`Failed to update ${config.name} config:`, error);
            toast.showToast(`Failed to update ${config.name} configuration. Please try again.`, "error");
        } finally {
            setIsSaving(false);
        }
    };

    const isConfigured = config.requiresConfig
        ? config.configFields?.every(field => {
            const value = method.config?.[field.key as keyof typeof method.config];
            return typeof value === 'string' && value.trim() !== '';
        })
        : true;

    const handleConfigChange = (key: string, value: string) => {
        setConfigValues(prev => ({ ...prev, [key]: value }));
    };

    return (
        <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
            <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-full ${config.iconBgColor} flex-shrink-0 flex items-center justify-center`}>
                            <div className={config.iconColor}>
                                {config.icon}
                            </div>
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">{config.name}</h3>
                    </div>
                    {isConfigured ? (
                        <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${method.enabled ? 'text-green-600' : 'text-gray-500'}`}>
                                {method.enabled ? 'Active' : 'Inactive'}
                            </span>
                            <button
                                onClick={onToggle}
                                disabled={isSaving}
                                className={`relative w-14 h-7 rounded-full transition duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${method.enabled ? "bg-blue-600" : "bg-gray-200"
                                    }`}
                            >
                                <div
                                    className={`absolute w-5 h-5 bg-white rounded-full shadow-md top-1 transition-all duration-200 ${method.enabled ? "right-1" : "left-1"
                                        }`}
                                ></div>
                            </button>
                        </div>
                    ) : !isEditing && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 text-sm font-medium"
                        >
                            Add
                        </button>
                    )}
                </div>

                <p className="text-sm text-gray-600 mb-4">{config.description}</p>

                {isEditing && config.requiresConfig ? (
                    <div className="space-y-4">
                        {config.configFields?.map(field => (
                            <div key={field.key}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
                                <input
                                    type={field.type}
                                    value={configValues[field.key] || ""}
                                    onChange={(e) => handleConfigChange(field.key, e.target.value)}
                                    placeholder={field.placeholder}
                                    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                                {field.example && (
                                    <p className="text-xs text-gray-500 mt-1">{field.example}</p>
                                )}
                            </div>
                        ))}
                        <div className="flex gap-3">
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                            >
                                {isSaving ? "Saving..." : "Save"}
                            </button>
                            <button
                                onClick={() => setIsEditing(false)}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : isConfigured && config.requiresConfig ? (
                    <div className="space-y-4">
                        <div className="border border-blue-200 rounded-lg p-4">
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-medium text-blue-800">Configuration</h3>
                                    <button
                                        onClick={() => setIsEditing(true)}
                                        className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 text-sm font-medium"
                                    >
                                        Edit
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {config.configFields?.map(field => (
                                        <p key={field.key} className="text-sm text-blue-700">
                                            {field.label}: {field.type === "password" ? "••••••••" : method.config?.[field.key as keyof typeof method.config] || "Not configured"}
                                        </p>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : null}

                {config.infoMessage && (
                    <div className={`${config.infoMessage.bgColor} border ${config.infoMessage.borderColor} rounded-lg p-4 mt-4`}>
                        <div className="flex items-start gap-3">
                            <AlertCircle className={`h-5 w-5 ${config.infoMessage.iconColor} mt-0.5 flex-shrink-0`} />
                            <div>
                                <h4 className={`text-sm font-medium ${config.infoMessage.textColor.replace('700', '800')} mb-1`}>
                                    {config.infoMessage.title}
                                </h4>
                                <p className={`text-sm ${config.infoMessage.textColor}`}>
                                    {config.infoMessage.message}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const PaymentMethods = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState({ title: "", message: "", onConfirm: () => { } });
    const { data: paymentMethods, isLoading, isError, refetch } = usePaymentMethods();
    const toast = useToastStore();

    const handleTogglePaymentMethod = async (methodId: string, isEnabled: boolean) => {
        setModalConfig({
            title: "Confirm Toggle",
            message: `Are you sure you want to ${isEnabled ? "disable" : "enable"} this payment method?`,
            onConfirm: async () => {
                try {
                    await PaymentService.enableDisablePaymentMethod(methodId);
                    refetch();
                    toast.showToast(`Payment method ${isEnabled ? "enabled" : "disabled"} successfully`, "success");
                } catch (error) {
                    console.error("Failed to toggle payment method:", error);
                    toast.showToast("Failed to update payment method status. Please try again.", "error");
                } finally {
                    setModalOpen(false);
                }
            },
        });
        setModalOpen(true);
    };

    if (isLoading) {
        return <div className="flex justify-center items-center h-screen">Loading...</div>;
    }

    if (isError) {
        return <div className="flex justify-center items-center h-screen text-red-600">Error loading payment methods</div>;
    }

    // Create default payment methods if not provided by backend
    const defaultPaymentMethods: PaymentMethod[] = [
        {
            _id: "razorpay-default",
            entityId: 1,
            name: "Razorpay",
            enabled: false,
            type: "RAZORPAY",
            config: {}
        },
        {
            _id: "phonepe-default",
            entityId: 1,
            name: "PhonePe",
            enabled: false,
            type: "PHONEPE",
            config: {}
        },
        {
            _id: "cod-default",
            entityId: 1,
            name: "Cash on Delivery",
            enabled: false,
            type: "COD",
            config: {}
        },
        {
            _id: "manual-upi-default",
            entityId: 1,
            name: "Manual UPI Payment",
            enabled: false,
            type: "MANUAL_UPI",
            config: {}
        }
    ];

    // Merge backend data with defaults
    const allPaymentMethods = defaultPaymentMethods.map(defaultMethod => {
        const backendMethod = paymentMethods?.find((method: PaymentMethod) => method.type === defaultMethod.type);
        return backendMethod || defaultMethod;
    });

    return (
        <div className="bg-gray-50 min-h-screen">
            <Modal
                isOpen={modalOpen}
                title={modalConfig.title}
                message={modalConfig.message}
                onConfirm={modalConfig.onConfirm}
                onCancel={() => setModalOpen(false)}
            />
            <div className="max-w-4xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
                {allPaymentMethods.map((method) => (
                    <PaymentMethodCard
                        key={method._id}
                        method={method}
                        onToggle={() => handleTogglePaymentMethod(method._id, !method.enabled)}
                    />
                ))}
            </div>
        </div>
    );
};

export default PaymentMethods; 